import { EmptyStatePanel, ErrorState, LoadingState, StatCard } from '@playforge/ui';
import { BlockStack, Button, ButtonGroup, Card, InlineGrid, Page, Text } from '@shopify/polaris';
import { useNavigate } from 'react-router-dom';
import { DashboardHero } from '../components/DashboardHero';
import { apiClient } from '../lib/api-client';
import { useApi } from '../lib/use-api';
import { type AnalyticsOverview, type CampaignView } from '../lib/types';

function pct(value: number): string {
  return `${Math.round(value * 100)}%`;
}

export function DashboardPage(): JSX.Element {
  const navigate = useNavigate();
  const { data, loading, error, refetch } = useApi(
    () => apiClient.get<AnalyticsOverview>('/analytics'),
    [],
  );
  // Whether the merchant has any campaign yet — drives the empty state so it
  // reflects setup progress, not just whether a game has been *played*.
  const campaigns = useApi(() => apiClient.get<CampaignView[]>('/campaigns?limit=1'), []);

  const hasActivity = (data?.gameStarts ?? 0) > 0;
  const hasCampaigns = (campaigns.data?.length ?? 0) > 0;

  const metrics: Array<{ label: string; value: string; help: string }> = [
    { label: 'Game starts', value: String(data?.gameStarts ?? 0), help: 'Games started' },
    { label: 'Game completes', value: String(data?.gameCompletes ?? 0), help: 'Games finished' },
    { label: 'Rewards won', value: String(data?.rewardsWon ?? 0), help: 'Rewards awarded' },
    { label: 'Coupons created', value: String(data?.couponsCreated ?? 0), help: 'Issued to players' },
    { label: 'Coupons used', value: String(data?.couponsUsed ?? 0), help: 'Redeemed at checkout' },
    {
      label: 'Completion rate',
      value: data ? pct(data.completionRate) : '0%',
      help: 'Completes / starts',
    },
  ];

  return (
    <Page
      title="Dashboard"
      subtitle="Your gamification performance at a glance"
      primaryAction={{ content: 'Create campaign', onAction: () => navigate('/campaigns') }}
    >
      <BlockStack gap="500">
        <DashboardHero
          title={
            hasActivity
              ? "Your game is live — here's how it's playing"
              : hasCampaigns
                ? 'Almost there — publish your campaign and enable the app embed'
                : 'Ready to start the game?'
          }
          subtitle={
            hasActivity
              ? 'Engage shoppers with interactive games and turn plays into purchases.'
              : hasCampaigns
                ? 'Publish your campaign, then turn PlayForge on in your theme (Help & Guide) to go live.'
                : 'Launch your first interactive game and start turning browsers into buyers.'
          }
          stats={[
            { num: String(data?.gameStarts ?? 0), label: '🎯 Plays' },
            { num: String(data?.rewardsWon ?? 0), label: '🎁 Rewards' },
            { num: data ? pct(data.completionRate) : '0%', label: '📈 Completion' },
          ]}
        />

        {loading ? <LoadingState label="Loading analytics" /> : null}
        {error ? <ErrorState message={error.message} onRetry={refetch} /> : null}

        {!loading ? (
          <InlineGrid columns={{ xs: 1, sm: 2, lg: 3 }} gap="400">
            {metrics.map((m) => (
              <StatCard key={m.label} label={m.label} value={m.value} helpText={m.help} />
            ))}
          </InlineGrid>
        ) : null}

        <Card>
          <BlockStack gap="300">
            <Text as="h2" variant="headingMd">
              Quick actions
            </Text>
            <ButtonGroup>
              <Button variant="primary" onClick={() => navigate('/campaigns')}>
                Create campaign
              </Button>
              <Button onClick={() => navigate('/analytics')}>View analytics</Button>
              <Button onClick={() => navigate('/billing')}>Upgrade plan</Button>
            </ButtonGroup>
          </BlockStack>
        </Card>

        {!loading && !campaigns.loading && !hasCampaigns ? (
          <Card>
            <EmptyStatePanel
              heading="Create your first campaign"
              primaryAction={{ content: 'Create campaign', onAction: () => navigate('/campaigns') }}
            >
              <p>
                Launch an interactive game — Spin Wheel, Scratch Card and more — to engage shoppers
                and grow revenue.
              </p>
            </EmptyStatePanel>
          </Card>
        ) : null}
      </BlockStack>
    </Page>
  );
}
